/**
 * Protected Audience API integration (Phase 8.2 follow-up).
 *
 * Chrome's FLEDGE / Protected Audience API replaces the third-party-cookie
 * retargeting flow with browser-orchestrated on-device auctions. Two
 * surfaces matter here:
 *
 *   - `navigator.joinAdInterestGroup(group, ttl)` — opt the user into a
 *     retargeting list. The browser will remember the group and run the
 *     bidder.js worklet whenever the publisher invokes runAdAuction next.
 *
 *   - `navigator.runAdAuction(config)` — execute an on-device sealed-bid
 *     auction across all interest groups the browser knows for the listed
 *     buyer origins. Returns an opaque urn (the publisher places it in a
 *     <fencedframe> / <iframe>) or null when nobody bids.
 *
 * Both calls are best-effort. Outside Chromium / when the user has the API
 * disabled they short-circuit to false/null without throwing — Protected
 * Audience is strictly additive demand, never blocking.
 *
 * The ad-server already advises an interest-group descriptor on
 * `AdResponse.fledge`. This module takes that descriptor and:
 *
 *   1. Auto-joins the user into the IG on render, so the *next* page that
 *      runs an auction can find the retargeting demand.
 *   2. Exposes `MpwaAds.runProtectedAudience(fledge)` for publishers that
 *      want to run the auction inline (header-bidding-style integration).
 */

import type { AdResponse } from './types.js';

type Fledge = NonNullable<AdResponse['fledge']>;

interface FledgeNavigator extends Navigator {
  joinAdInterestGroup?: (group: Record<string, unknown>, ttlSeconds?: number) => Promise<void> | void;
  leaveAdInterestGroup?: (group: Record<string, unknown>) => Promise<void> | void;
  runAdAuction?: (config: Record<string, unknown>) => Promise<string | null>;
}

// Spec maximum is 30 days; pick a slightly shorter default so a long-tail
// publisher integration doesn't keep stale IGs around indefinitely. The
// ad-server's `fledge` descriptor is per-impression so the freshest one
// always wins via name uniqueness.
const DEFAULT_TTL_SECONDS = 14 * 86400;

export interface JoinOpts {
  /** TTL the IG should live for. Capped at 30 days by the browser. */
  ttlSeconds?: number;
  /**
   * Hint bid price (USD CPM) the IG carries on userBiddingSignals — the
   * bidder.js worklet reads this verbatim. Without it the worklet returns
   * bid: 0 and the IG simply doesn't participate in the next auction.
   */
  bid?: number;
  /**
   * Render URL the IG should remember (the creative landing markup). Today
   * the ad-server emits the creative HTML on the AdSlot rather than as a
   * pre-rendered URL, so most callers leave this empty and rely on inline
   * render — the IG exists for measurement / future retargeting.
   */
  renderURL?: string;
}

/**
 * Opt the visitor into the IG the ad-server advised. Fire-and-forget,
 * resolves to `false` on any failure. Safe to call on every render.
 */
export async function joinFledgeInterestGroup(
  fledge: Fledge | undefined,
  opts: JoinOpts = {},
): Promise<boolean> {
  const n = pickNavigator();
  if (!n || !fledge || !fledge.owner || !fledge.name) return false;
  if (typeof n.joinAdInterestGroup !== 'function') return false;
  try {
    const group: Record<string, unknown> = {
      owner: fledge.owner,
      name: fledge.name,
      biddingLogicURL: fledge.biddingLogicUrl,
    };
    if (opts.renderURL) {
      group.ads = [{ renderURL: opts.renderURL }];
    }
    if (opts.bid !== undefined) {
      group.userBiddingSignals = { bid: opts.bid };
    }
    // Phase 8.2 follow-up — propagate the trusted-bidding-signals
    // descriptor when the ad-server advised one. The browser will fetch
    // this URL before every auction so the bidder.js worklet sees fresh
    // campaign status (running / paused / exhausted) instead of the
    // stale snapshot baked in at IG join time.
    if (fledge.trustedBiddingSignalsUrl) {
      group.trustedBiddingSignalsURL = fledge.trustedBiddingSignalsUrl;
    }
    if (fledge.trustedBiddingSignalsKeys && fledge.trustedBiddingSignalsKeys.length > 0) {
      group.trustedBiddingSignalsKeys = fledge.trustedBiddingSignalsKeys;
    }
    await n.joinAdInterestGroup(group, opts.ttlSeconds ?? DEFAULT_TTL_SECONDS);
    return true;
  } catch {
    // ARA / FLEDGE are measurement-grade — never propagate errors into the
    // ad-render path.
    return false;
  }
}

export interface RunOpts {
  /**
   * Additional buyer origins the browser should poll for interest groups.
   * Defaults to [fledge.owner] so single-seller, single-buyer auctions
   * (the mpwa.to default) Just Work.
   */
  buyers?: string[];
  /**
   * When true, returns a FencedFrameConfig instead of a urn so the
   * publisher can pin the result into <fencedframe config={...}>.
   * Off by default for broader compatibility.
   */
  resolveToConfig?: boolean;
}

/**
 * Run a Protected Audience auction using the advisory descriptor the
 * ad-server returned. Resolves to the winning urn (placeable in a
 * fencedframe / iframe src) or null if the browser declined / nobody bid.
 */
export async function runProtectedAudience(
  fledge: Fledge | undefined,
  opts: RunOpts = {},
): Promise<string | null> {
  const n = pickNavigator();
  if (!n || !fledge || !fledge.owner) return null;
  if (typeof n.runAdAuction !== 'function') return null;
  try {
    const config: Record<string, unknown> = {
      seller: fledge.owner,
      decisionLogicURL: fledge.decisionLogicUrl,
      interestGroupBuyers: opts.buyers && opts.buyers.length > 0 ? opts.buyers : [fledge.owner],
    };
    if (opts.resolveToConfig) {
      config.resolveToConfig = true;
    }
    const result = await n.runAdAuction(config);
    return typeof result === 'string' && result.length > 0 ? result : null;
  } catch {
    return null;
  }
}

function pickNavigator(): FledgeNavigator | null {
  if (typeof navigator === 'undefined') return null;
  return navigator as FledgeNavigator;
}
